import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Loader2, ShieldCheck, Mail, CheckCircle2, Clock, Download, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError, fmtDate } from "@/lib/api";

const textOn = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6 ? "#0f172a" : "#ffffff";
};

const Preview = ({ name, color, logo, fallback }) => (
  <div data-testid="brand-preview" className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white p-5 text-slate-900" style={{ borderTop: `4px solid ${color}` }}>
    {logo ? <img src={logo} alt={name || fallback} className="h-10 max-w-[240px] object-contain" /> : <p className="font-bold text-lg" style={{ color }}>{name || fallback}</p>}
    <p className="text-[10px] uppercase tracking-wider text-emerald-600 mt-1 flex items-center gap-1"><ShieldCheck size={11} /> Verified by PostalCloud</p>
    <p className="text-sm mt-4">Hello Jane, <strong>{name || fallback}</strong> has sent you a verified letter.</p>
    <span className="inline-block mt-4 px-4 py-2 rounded-lg text-sm font-bold" style={{ background: color, color: textOn(color) }}>Claim your letter</span>
  </div>
);

const VerifyLink = ({ path }) => {
  const url = `${window.location.origin}${path}`;
  return (
    <div data-testid="brand-verify-link" className="rounded-xl border border-slate-200 dark:border-slate-800 p-4">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your branded verify page</p>
      <p className="font-mono text-xs break-all mt-1" data-testid="brand-verify-url">{url}</p>
      <div className="flex gap-2 mt-3">
        <button data-testid="brand-verify-copy-btn" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"><Copy size={12} /> Copy</button>
        <a data-testid="brand-verify-open-link" href={path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"><ExternalLink size={12} /> Open</a>
      </div>
      <p className="text-xs text-slate-500 mt-2">Share this on your website or letters. Recipients can check your certificates there, and it's linked from every notification email.</p>
    </div>
  );
};

export const BrandingPanel = ({ org, onSaved }) => {
  const b = org.brand;
  const [name, setName] = useState(b.display_name);
  const [color, setColor] = useState(b.color);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { onSaved((await fn()).data); toast.success(msg); } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };
  const upload = (f) => {
    if (!f) return;
    if (f.size > 1024 * 1024) return toast.error("Logo must be 1 MB or smaller");
    const fd = new FormData();
    fd.append("file", f);
    run("logo", () => api.post("/org/branding/logo", fd), "Logo uploaded");
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="branding-panel">
      <div className="space-y-4">
        <div className="space-y-2"><Label htmlFor="brand-name">Display name ("From")</Label>
          <Input id="brand-name" data-testid="brand-name-input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder={org.name} className="h-10" /></div>
        <div className="space-y-2"><Label htmlFor="brand-color">Brand colour</Label>
          <div className="flex items-center gap-3">
            <input id="brand-color" data-testid="brand-color-input" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-14 rounded border border-slate-200 dark:border-slate-700 bg-transparent cursor-pointer" />
            <span className="font-mono text-xs">{color}</span>
          </div></div>
        <button data-testid="brand-save-btn" disabled={!!busy} onClick={() => run("save", () => api.patch("/org/branding", { display_name: name, color }), "Branding saved")} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60">
          {busy === "save" && <Loader2 size={14} className="animate-spin" />} Save name & colour
        </button>
        <div className="space-y-2 pt-2"><Label>Logo (PNG or JPG, max 1 MB)</Label>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} data-testid="brand-logo-input" type="file" accept="image/png,image/jpeg" className="sr-only" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
            <button data-testid="brand-logo-upload-btn" disabled={!!busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60">
              {busy === "logo" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {b.logo_url ? "Replace logo" : "Upload logo"}</button>
            {b.logo_url && <button data-testid="brand-logo-remove-btn" disabled={!!busy} onClick={() => run("rm", () => api.delete("/org/branding/logo"), "Logo removed")} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 size={14} /> Remove</button>}
          </div></div>
        <p className="text-xs text-slate-500">Applied to recipient emails, claim pages, delivery certificates and the public verify page for letters sent from now on. Emails are sent as "PostalCloud" with your name, logo and colours inside, or from your own domain once you verify one below. A "Verified by PostalCloud" mark always stays. Every change is recorded in the audit trail.{b.updated_at && ` Last changed ${fmtDate(b.updated_at)} by ${b.updated_by_name}.`}</p>
      </div>
      <div className="space-y-4">
        <Preview name={name} color={color} logo={b.logo_url} fallback={org.name} />
        {b.verify_url && <VerifyLink path={b.verify_url} />}
      </div>
    </div>
  );
};

export const ReplyToPanel = ({ org, onSaved }) => {
  const rt = org.reply_to || {};
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { onSaved((await fn()).data); toast.success(msg); setEmail(""); } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-4" data-testid="reply-to-panel">
      {rt.email ? (
        <p data-testid="reply-to-current" className="text-sm flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-600" /> Replies go to <span className="font-semibold">{rt.email}</span> <span className="text-xs text-slate-500">verified {fmtDate(rt.verified_at)}</span></p>
      ) : <p data-testid="reply-to-none" className="text-sm text-slate-500">No reply-to inbox. Recipients' replies are not delivered anywhere.</p>}
      {rt.pending_email && <p data-testid="reply-to-pending" className="text-sm text-amber-700 flex items-center gap-2"><Clock size={15} /> Waiting for confirmation from {rt.pending_email} (link expires {fmtDate(rt.pending_expires_at)})</p>}
      <form className="flex flex-wrap gap-3" onSubmit={(e) => { e.preventDefault(); run("set", () => api.post("/org/reply-to", { email }), `Confirmation sent to ${email}`); }}>
        <Input data-testid="reply-to-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="letters@yourorganisation.org" className="h-10 flex-1 min-w-[220px]" />
        <button data-testid="reply-to-submit-btn" disabled={!!busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60">
          {busy === "set" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {rt.email ? "Change inbox" : "Send confirmation"}</button>
        {(rt.email || rt.pending_email) && <button type="button" data-testid="reply-to-remove-btn" disabled={!!busy} onClick={() => window.confirm("Stop sending recipients' replies to this inbox?") && run("rm", () => api.delete("/org/reply-to"), "Reply-to inbox removed")} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 size={14} /> Remove</button>}
      </form>
      <p className="text-xs text-slate-500">We email a confirmation link to the inbox (valid 24 hours). Only a confirmed inbox is used, for letters sent from then on. Changes need a new confirmation, and every step is recorded in the audit trail.</p>
    </div>
  );
};

export const BrandingHistory = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/org/branding-history").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const exportCsv = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/org/branding-history.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      Object.assign(document.createElement("a"), { href: url, download: "PostalCloud-branding-history.csv" }).click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!rows) return <Loader2 className="animate-spin text-emerald-600" />;
  return (
    <div data-testid="branding-history">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs text-slate-500">Read directly from the tamper-evident audit trail. Ownership and member role changes stay in the full audit trail.</p>
        <button data-testid="branding-history-export-btn" onClick={exportCsv} disabled={busy} className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export CSV</button>
      </div>
      {!rows.length && <p data-testid="branding-history-empty" className="text-sm text-slate-500">No changes yet.</p>}
      <ol className="relative border-l border-slate-200 dark:border-slate-800 ml-2 space-y-5">
        {rows.map((r) => (
          <li key={r.seq} data-testid={`branding-history-${r.seq}`} className="ml-5">
            <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-emerald-600" />
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{r.category} · <span className="font-mono normal-case">{fmtDate(r.timestamp)}</span></p>
            <p className="text-sm font-semibold mt-0.5">{r.summary}</p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              {r.logo_url && <img src={r.logo_url} alt="Logo version" className="h-8 max-w-[140px] object-contain rounded border border-slate-200 bg-white p-0.5" />}
              <p className="text-xs text-slate-600 dark:text-slate-400 break-all"><span className="line-through decoration-slate-400">{r.before}</span> → <span className="font-medium">{r.after}</span></p>
            </div>
            <p className="text-xs text-slate-500 mt-1">By {r.actor_name}{r.actor_email && ` (${r.actor_email})`} · <span className="capitalize">{r.actor_role}</span> · audit #{r.seq}</p>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const TransferPanel = ({ org, onDone }) => {
  const admins = org.members.filter((m) => m.org_role === "admin");
  const [to, setTo] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  if (!admins.length) return <p className="text-sm text-slate-500" data-testid="transfer-no-admins">Promote a member to Admin first. Ownership can only be transferred to an Admin.</p>;
  const submit = async (e) => {
    e.preventDefault();
    const target = admins.find((a) => a.id === to);
    if (!window.confirm(`Transfer ownership of ${org.name} to ${target.name}? You will become an Admin and lose access to billing and API keys.`)) return;
    setBusy(true);
    try {
      await api.post("/org/transfer", { new_owner_id: to, password });
      toast.success(`${target.name} is now the owner`);
      setPassword("");
      onDone();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="transfer-form">
      <select data-testid="transfer-target-select" required value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-full px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm">
        <option value="">Choose an Admin…</option>
        {admins.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
      </select>
      <Input data-testid="transfer-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="h-10" />
      <button data-testid="transfer-submit-btn" disabled={busy || !to} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-60">
        {busy && <Loader2 size={14} className="animate-spin" />} Transfer ownership
      </button>
      <p className="text-xs text-slate-500">You'll become an Admin. Both of you get an email, active API keys move to the new owner, and the transfer is recorded in the audit trail. Only the new owner can transfer it back.</p>
    </form>
  );
};
